/*
 * Fullscreen product image zoom viewer.
 *
 * Replaces Dawn's lightbox for IMAGE media on the product page with a viewer
 * that supports:
 *   - "+" / "-" buttons (and a reset)
 *   - pinch-to-zoom (two fingers)
 *   - mouse-wheel zoom (toward the cursor)
 *   - drag to pan while zoomed
 *   - double-tap / double-click to toggle zoom
 *   - swipe / arrow keys to move between images while not zoomed
 *
 * Video and 3D media still open Dawn's default modal.
 */
(function () {
  const MIN_SCALE = 1;
  const MAX_SCALE = 5;
  const DOUBLE_TAP_SCALE = 2.5;

  const T = {
    close: 'Închide',
    zoomIn: 'Mărește',
    zoomOut: 'Micșorează',
    reset: 'Resetează zoomul',
    prev: 'Imaginea anterioară',
    next: 'Imaginea următoare',
  };

  function hiRes(url, width) {
    if (!url) return url;
    try {
      const u = new URL(url, window.location.href);
      u.searchParams.set('width', String(width));
      return u.toString();
    } catch (e) {
      return url;
    }
  }

  class ProductImageZoom {
    constructor(gallery) {
      this.gallery = gallery;
      this.images = [];
      this.index = 0;
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      this.pointers = new Map();
      this.pinchStart = null;
      this.lastTap = 0;
      this.opener = null;

      this.collectImages();
      if (!this.images.length) return;

      this.bindOpeners();
    }

    collectImages() {
      const containers = this.gallery.querySelectorAll(
        '.product-media-container.media-type-image, [data-media-type="image"]'
      );
      const seen = new Set();
      containers.forEach((container) => {
        const img = container.querySelector('img');
        if (!img) return;
        const toggle = container.querySelector('.product__media-toggle, [aria-haspopup="dialog"]');
        const base = img.getAttribute('src') || img.currentSrc;
        if (!base || seen.has(base)) return;
        seen.add(base);
        this.images.push({
          src: hiRes(base, 2048),
          alt: img.getAttribute('alt') || '',
          toggle: toggle,
        });
      });
    }

    bindOpeners() {
      this.gallery.addEventListener(
        'click',
        (event) => {
          const toggle = event.target.closest('.product__media-toggle, [aria-haspopup="dialog"]');
          if (!toggle) return;
          const container = toggle.closest('.product-media-container');
          if (container && !container.classList.contains('media-type-image')) return;

          const idx = this.images.findIndex((im) => im.toggle === toggle);
          if (idx === -1) return;

          event.preventDefault();
          event.stopImmediatePropagation();
          this.open(idx, toggle);
        },
        true
      );
    }

    open(index, opener) {
      this.index = index;
      this.opener = opener || document.activeElement;
      if (!this.overlay) this.build();
      document.body.appendChild(this.overlay);
      document.documentElement.style.overflow = 'hidden';
      this.overlay.hidden = false;
      // force reflow so the transition runs
      // eslint-disable-next-line no-unused-expressions
      this.overlay.offsetWidth;
      this.overlay.classList.add('is-open');
      this.render();
      this.closeButton.focus();
      document.addEventListener('keydown', this.onKeydown);
    }

    close() {
      this.overlay.classList.remove('is-open');
      document.removeEventListener('keydown', this.onKeydown);
      document.documentElement.style.overflow = '';
      const done = () => {
        this.overlay.hidden = true;
        if (this.overlay.parentNode) this.overlay.parentNode.removeChild(this.overlay);
        this.overlay.removeEventListener('transitionend', done);
      };
      this.overlay.addEventListener('transitionend', done);
      setTimeout(done, 300);
      if (this.opener && typeof this.opener.focus === 'function') this.opener.focus();
    }

    build() {
      this.onKeydown = (e) => {
        if (e.key === 'Escape') this.close();
        else if (e.key === 'ArrowRight') this.step(1);
        else if (e.key === 'ArrowLeft') this.step(-1);
        else if (e.key === '+' || e.key === '=') this.zoomBy(1.4);
        else if (e.key === '-' || e.key === '_') this.zoomBy(1 / 1.4);
      };

      const overlay = document.createElement('div');
      overlay.className = 'pdp-zoom';
      overlay.hidden = true;
      overlay.setAttribute('role', 'dialog');
      overlay.setAttribute('aria-modal', 'true');
      overlay.innerHTML = `
        <button type="button" class="pdp-zoom__close" aria-label="${T.close}">
          <svg viewBox="0 0 20 20" width="20" height="20" aria-hidden="true"><path d="M4 4l12 12M16 4L4 16" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round"/></svg>
        </button>
        <button type="button" class="pdp-zoom__nav pdp-zoom__nav--prev" aria-label="${T.prev}">
          <svg viewBox="0 0 20 20" width="22" height="22" aria-hidden="true"><path d="M13 3L6 10l7 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <button type="button" class="pdp-zoom__nav pdp-zoom__nav--next" aria-label="${T.next}">
          <svg viewBox="0 0 20 20" width="22" height="22" aria-hidden="true"><path d="M7 3l7 7-7 7" stroke="currentColor" stroke-width="2" fill="none" stroke-linecap="round" stroke-linejoin="round"/></svg>
        </button>
        <div class="pdp-zoom__stage">
          <img class="pdp-zoom__img" alt="" draggable="false">
        </div>
        <div class="pdp-zoom__controls" role="group" aria-label="Zoom">
          <button type="button" class="pdp-zoom__btn" data-zoom="out" aria-label="${T.zoomOut}">&minus;</button>
          <button type="button" class="pdp-zoom__btn pdp-zoom__level" data-zoom="reset" aria-label="${T.reset}">100%</button>
          <button type="button" class="pdp-zoom__btn" data-zoom="in" aria-label="${T.zoomIn}">+</button>
        </div>
      `;

      this.overlay = overlay;
      this.stage = overlay.querySelector('.pdp-zoom__stage');
      this.img = overlay.querySelector('.pdp-zoom__img');
      this.closeButton = overlay.querySelector('.pdp-zoom__close');
      this.levelButton = overlay.querySelector('.pdp-zoom__level');
      this.prevButton = overlay.querySelector('.pdp-zoom__nav--prev');
      this.nextButton = overlay.querySelector('.pdp-zoom__nav--next');

      this.closeButton.addEventListener('click', () => this.close());
      this.prevButton.addEventListener('click', () => this.step(-1));
      this.nextButton.addEventListener('click', () => this.step(1));
      overlay.querySelectorAll('[data-zoom]').forEach((btn) => {
        btn.addEventListener('click', () => {
          const kind = btn.getAttribute('data-zoom');
          if (kind === 'in') this.zoomBy(1.5);
          else if (kind === 'out') this.zoomBy(1 / 1.5);
          else this.reset();
        });
      });

      // Tapping the dimmed area outside the image closes the viewer — never on
      // the image itself, and never at the end of a drag/pinch.
      this.stage.addEventListener('click', (e) => {
        if (this.suppressClick) {
          this.suppressClick = false;
          return;
        }
        const r = this.img.getBoundingClientRect();
        const outside =
          e.clientX < r.left || e.clientX > r.right || e.clientY < r.top || e.clientY > r.bottom;
        if (outside) this.close();
      });

      this.stage.addEventListener('wheel', this.onWheel.bind(this), { passive: false });
      this.stage.addEventListener('pointerdown', this.onPointerDown.bind(this));
      this.stage.addEventListener('pointermove', this.onPointerMove.bind(this));
      this.stage.addEventListener('pointerup', this.onPointerUp.bind(this));
      this.stage.addEventListener('pointercancel', this.onPointerUp.bind(this));
      this.stage.addEventListener('dblclick', this.onDoubleClick.bind(this));
    }

    render() {
      const image = this.images[this.index];
      this.img.src = image.src;
      this.img.alt = image.alt;
      this.reset(true);
      const multiple = this.images.length > 1;
      this.prevButton.hidden = !multiple;
      this.nextButton.hidden = !multiple;
    }

    step(dir) {
      if (this.images.length < 2) return;
      this.index = (this.index + dir + this.images.length) % this.images.length;
      this.render();
    }

    reset(skipRender) {
      this.scale = 1;
      this.tx = 0;
      this.ty = 0;
      if (!skipRender) this.apply();
      else this.apply();
    }

    clampPan() {
      const rect = this.stage.getBoundingClientRect();
      const iw = this.img.clientWidth * this.scale;
      const ih = this.img.clientHeight * this.scale;
      const maxX = Math.max(0, (iw - rect.width) / 2);
      const maxY = Math.max(0, (ih - rect.height) / 2);
      this.tx = Math.min(maxX, Math.max(-maxX, this.tx));
      this.ty = Math.min(maxY, Math.max(-maxY, this.ty));
    }

    apply() {
      this.clampPan();
      this.img.style.transform = `translate(${this.tx}px, ${this.ty}px) scale(${this.scale})`;
      this.stage.classList.toggle('is-zoomed', this.scale > 1.01);
      this.levelButton.textContent = Math.round(this.scale * 100) + '%';
      this.overlay.classList.toggle('pdp-zoom--zoomed', this.scale > 1.01);
    }

    zoomAt(nextScale, clientX, clientY) {
      nextScale = Math.min(MAX_SCALE, Math.max(MIN_SCALE, nextScale));
      const rect = this.stage.getBoundingClientRect();
      const cx = clientX - rect.left - rect.width / 2;
      const cy = clientY - rect.top - rect.height / 2;
      const ratio = nextScale / this.scale;
      this.tx = cx - (cx - this.tx) * ratio;
      this.ty = cy - (cy - this.ty) * ratio;
      this.scale = nextScale;
      if (this.scale <= MIN_SCALE + 0.001) {
        this.tx = 0;
        this.ty = 0;
      }
      this.apply();
    }

    zoomBy(factor) {
      const rect = this.stage.getBoundingClientRect();
      this.zoomAt(this.scale * factor, rect.left + rect.width / 2, rect.top + rect.height / 2);
    }

    onWheel(event) {
      event.preventDefault();
      const factor = event.deltaY < 0 ? 1.12 : 1 / 1.12;
      this.zoomAt(this.scale * factor, event.clientX, event.clientY);
    }

    onDoubleClick(event) {
      event.preventDefault();
      const target = this.scale > 1.01 ? MIN_SCALE : DOUBLE_TAP_SCALE;
      this.zoomAt(target, event.clientX, event.clientY);
    }

    onPointerDown(event) {
      this.stage.setPointerCapture(event.pointerId);
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 2) {
        const pts = [...this.pointers.values()];
        this.pinchStart = {
          dist: Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y),
          scale: this.scale,
          midX: (pts[0].x + pts[1].x) / 2,
          midY: (pts[0].y + pts[1].y) / 2,
        };
      } else if (this.pointers.size === 1) {
        // Manual double-tap only for touch; mouse gets the native dblclick.
        if (event.pointerType === 'touch') {
          const now = Date.now();
          if (now - this.lastTap < 300) {
            this.onDoubleClick(event);
            this.lastTap = 0;
          } else {
            this.lastTap = now;
          }
        }
        this.dragStart = { x: event.clientX, y: event.clientY, tx: this.tx, ty: this.ty };
        this.swipeStart = { x: event.clientX, y: event.clientY, t: Date.now() };
      }
    }

    onPointerMove(event) {
      if (!this.pointers.has(event.pointerId)) return;
      this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });

      if (this.pointers.size === 2 && this.pinchStart) {
        this.suppressClick = true;
        const pts = [...this.pointers.values()];
        const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
        const midX = (pts[0].x + pts[1].x) / 2;
        const midY = (pts[0].y + pts[1].y) / 2;
        this.zoomAt((dist / this.pinchStart.dist) * this.pinchStart.scale, midX, midY);
        return;
      }

      if (this.pointers.size === 1 && this.dragStart) {
        const dx = event.clientX - this.dragStart.x;
        const dy = event.clientY - this.dragStart.y;
        if (Math.abs(dx) + Math.abs(dy) > 6) this.suppressClick = true;
        if (this.scale > 1.01) {
          this.tx = this.dragStart.tx + dx;
          this.ty = this.dragStart.ty + dy;
          this.apply();
        }
      }
    }

    onPointerUp(event) {
      this.pointers.delete(event.pointerId);
      if (this.pointers.size < 2) this.pinchStart = null;

      // Touch simulators / interrupted gestures can drop the matching pointerup;
      // once the last real touch ends, forget any strays.
      if (event.type === 'pointercancel') this.pointers.clear();

      if (this.pointers.size === 0) {
        // swipe to change image when not zoomed
        if (this.swipeStart && this.scale <= 1.01) {
          const dx = event.clientX - this.swipeStart.x;
          const dy = event.clientY - this.swipeStart.y;
          const dt = Date.now() - this.swipeStart.t;
          if (Math.abs(dx) > 60 && Math.abs(dx) > Math.abs(dy) * 1.5 && dt < 600) {
            this.step(dx < 0 ? 1 : -1);
          }
        }
        this.dragStart = null;
        this.swipeStart = null;
      }
    }
  }

  function init() {
    document.querySelectorAll('media-gallery').forEach((gallery) => {
      if (gallery.dataset.zoomViewer === 'on') return;
      gallery.dataset.zoomViewer = 'on';
      new ProductImageZoom(gallery);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
